import {useCallback, useRef, useState} from 'react';
import {getAudioInfo} from '../../lib/audioFile';
import {formatBytes} from '../../lib/audioMetadata';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {uploadAudioFile, resumeAudioUpload} from '../../store/slices/audioThunks';
import {clearUploadError} from '../../store/slices/audioSlice';
import {
  uploadPhaseSelector,
  uploadProgressSelector,
} from '../../store/selectors/audio.selectors';
import {ApiError} from '../../lib/apiError';
import {AudioUpload} from '../../domain/types';
import {PickedAudioFile, rememberFileHandle} from '../../lib/fileHandles';
import {DropzoneUploadState} from './UploadDropzone';

/** What each phase of the upload is called while the user is watching it. */
const PHASE_LABEL: Record<string, string> = {
  requesting: 'Preparing upload…',
  resuming: 'Checking what already uploaded…',
  uploading: 'Uploading',
  finalizing: 'Confirming with the server…',
};

interface UseAudioUploadQueueOptions {
  projectId?: string;
  maxFileBytes: number;
  onUploaded: (audio: AudioUpload) => void;
  onError: (message: string) => void;
  onPaymentRequired: (error: ApiError) => void;
  /**
   * The audio list no longer matches the server and should be re-fetched.
   *
   * Two occasions, opposite directions. An upload that stopped part-way left a resumable row that
   * was never in the list — rows only enter it once they are `ready` — and without a re-fetch the
   * file silently vanishes from the workspace until reload, which is the exact failure resumable
   * uploads exist to stop. And a resume answered 404 means the row is gone from under us, so it has
   * to leave the list rather than sit there offering a button that will never work.
   */
  onUploadListStale: () => void;
  /**
   * A resume the server refused outright — the wrong file, or an upload that cannot be carried on.
   * Kept separate from `onError` because neither is a toast: both need an offer of what to do
   * instead, and the picked file has to survive to be the thing that is offered.
   */
  onResumeRefused: (refusal: {audio: AudioUpload; picked: PickedAudioFile; error: ApiError}) => void;
}

/**
 * Dropping five files should upload five files.
 *
 * The transfer itself stays sequential — `audioSlice.upload` tracks one in-flight upload, and five
 * files going at once would divide the same connection five ways while making the progress bar
 * meaningless. So the queue is the only thing that is plural: files are probed and sent one at a
 * time, the dropzone reports "3 of 5", and a failure stops the run rather than silently skipping
 * ahead to the next file. (Within one file the parts *are* concurrent — that is
 * `uploadPartBatch`'s job, and it is bounded by one file's worth of bandwidth.)
 *
 * Resuming runs through the same hook, so the dropzone shows a resumed upload exactly like a fresh
 * one, starting at whatever fraction R2 already holds.
 */
export function useAudioUploadQueue({
  projectId,
  maxFileBytes,
  onUploaded,
  onError,
  onPaymentRequired,
  onUploadListStale,
  onResumeRefused,
}: UseAudioUploadQueueOptions) {
  const dispatch = useAppDispatch();
  const phase = useAppSelector(uploadPhaseSelector);
  const progress = useAppSelector(uploadProgressSelector);

  const [queue, setQueue] = useState<{fileName: string; index: number; total: number} | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const cancelledRef = useRef(false);

  const uploadFiles = useCallback(async (picked: PickedAudioFile[]) => {
    cancelledRef.current = false;
    dispatch(clearUploadError());

    for (let index = 0; index < picked.length; index += 1) {
      if (cancelledRef.current) {
        break;
      }

      const {file, handle} = picked[index];
      setQueue({fileName: file.name, index: index + 1, total: picked.length});

      if (file.size > maxFileBytes) {
        onError(`${file.name} is ${formatBytes(file.size)} — the limit on your plan is ${formatBytes(maxFileBytes)}.`);
        break;
      }

      // Duration and sample rate are read here, before anything is sent: the upload endpoint gates
      // on duration, and `sampleRate` is the field /convertxml needs that nothing else can supply.
      let info;
      try {
        info = await getAudioInfo(file);
      } catch (error) {
        onError(
          error instanceof Error && error.message
            ? `${file.name}: ${error.message}`
            : `Could not read ${file.name} — it may not be a supported audio format.`
        );
        break;
      }

      if (cancelledRef.current) {
        break;
      }

      const controller = new AbortController();
      abortRef.current = controller;

      const result = await dispatch(uploadAudioFile({
        file,
        projectId,
        durationSeconds: info.durationSeconds,
        durationTicks: Number(info.durationTicks), // BigInt does not survive JSON.stringify
        sampleRate: info.sampleRate,
        signal: controller.signal,
      }));

      abortRef.current = null;

      if (uploadAudioFile.fulfilled.match(result)) {
        onUploaded(result.payload);
        continue;
      }

      const error = result.payload;

      // Remembering the handle only pays off for an upload that did *not* finish, which is exactly
      // this branch: it is what lets Resume re-open the file without a second trip to the picker.
      // Chromium only; everywhere else this is a no-op and Resume asks for the file.
      if (error?.audioId && handle) {
        void rememberFileHandle(error.audioId, handle);
      }

      // Including a cancel: stopping an upload now means "not right now", not "throw it away", so
      // the half-finished row belongs on screen with its Resume button either way.
      if (error?.audioId) {
        onUploadListStale();
      }

      if (error?.status === 402) {
        onPaymentRequired(error);
      } else if (error && error.message !== 'Upload cancelled') {
        onError(error.message);
      }

      // One failure stops the run. Pushing on would bury the reason under the next file's progress.
      break;
    }

    setQueue(null);
  }, [dispatch, projectId, maxFileBytes, onUploaded, onError, onPaymentRequired, onUploadListStale]);

  /**
   * Carry on an upload that stopped part-way, given the same file again.
   *
   * Nothing is probed and no size check runs: this upload was already accepted once, its part size
   * is fixed, and whether the file matches the one it started with is the server's call — see the
   * `/parts` guard — not something to second-guess from the browser.
   */
  const resumeUpload = useCallback(async (audio: AudioUpload, picked: PickedAudioFile) => {
    cancelledRef.current = false;
    dispatch(clearUploadError());
    setQueue({fileName: picked.file.name, index: 1, total: 1});

    const controller = new AbortController();
    abortRef.current = controller;

    const result = await dispatch(resumeAudioUpload({
      audio,
      file: picked.file,
      signal: controller.signal,
    }));

    abortRef.current = null;
    setQueue(null);

    if (resumeAudioUpload.fulfilled.match(result)) {
      // The thunk drops the remembered handle itself — the file is in R2, there is nothing left to
      // re-open it for.
      onUploaded(result.payload);
      return;
    }

    const error = result.payload;

    if (!error || error.message === 'Upload cancelled') {
      return;
    }

    // The row is gone: the 48-hour sweep reached it, or it was deleted in another tab. There is
    // nothing to offer and nothing to delete — the row just has to stop being on screen.
    if (error.status === 404) {
      onUploadListStale();
      onError(`${audio.name} was cleared — unfinished uploads are swept up after two days. Upload it again.`);
      return;
    }

    // The two refusals the workspace has a real answer for: the wrong file, and an upload that
    // cannot be continued at all. Everything else — a dropped connection part-way through — is an
    // ordinary failure, and the row stays resumable so trying again is the fix.
    if (error.fileMismatch || error.resumable === false) {
      onResumeRefused({audio, picked, error});
      return;
    }

    // Worth keeping the handle: the row is still resumable and the file has not moved.
    void rememberFileHandle(audio.id, picked.handle);
    onError(error.message);
  }, [dispatch, onUploaded, onError, onUploadListStale, onResumeRefused]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    abortRef.current?.abort();
  }, []);

  const uploadState: DropzoneUploadState | null = queue
    ? {
      fileName: queue.fileName,
      phaseLabel: PHASE_LABEL[phase] ?? 'Uploading',
      percent: phase === 'uploading' ? progress.percent : -1,
      loaded: progress.loaded,
      total: progress.total,
      positionLabel: queue.total > 1 ? `File ${queue.index} of ${queue.total}` : '',
    }
    : null;

  return {uploadFiles, resumeUpload, cancel, uploadState, isUploading: queue !== null};
}
